import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { useVegaEmbed } from "react-vega";
import { Warn, type LoggerInterface, type View } from "vega";
import type { EmbedOptions, Result, VisualizationSpec } from "vega-embed";
import { createBlockedLoader } from "../custom/loader.js";

export interface SafeEmbedOptionsInput {
  mode: "vega-lite" | "vega";
  renderer: "svg" | "canvas";
  actions: boolean;
  tooltip: boolean;
  isDark: boolean;
  patch?: EmbedOptions["patch"];
  config?: EmbedOptions["config"];
}

/**
 * The embed options every chart renders with. Expressions are interpreted
 * from their AST (`ast: true`) — Vega's default compiles them with `new
 * Function`, which the app's CSP (no 'unsafe-eval') forbids. The loader
 * refuses every outside resource. Tooltips use vega-tooltip, which renders a
 * fixed-position element on <body>, so a clipping grid cell never cuts it off.
 */
export function useSafeEmbedOptions({
  mode,
  renderer,
  actions,
  tooltip,
  isDark,
  patch,
  config,
}: SafeEmbedOptionsInput): EmbedOptions {
  // react-vega re-embeds when options change by deep equality, and functions
  // compare by reference — the loader must be stable across renders.
  const loader = useMemo(() => createBlockedLoader(), []);
  return useMemo<EmbedOptions>(
    () => ({
      mode,
      renderer,
      actions,
      ast: true,
      loader,
      tooltip: tooltip ? { theme: isDark ? "dark" : "light" } : false,
      ...(patch ? { patch } : {}),
      ...(config ? { config } : {}),
    }),
    [mode, renderer, actions, loader, tooltip, isDark, patch, config],
  );
}

/**
 * A Vega logger that hands every error to `onError` and otherwise stays quiet
 * (Vega's default writes warnings to the console for authors to see).
 */
export function forwardingLogger(
  onError: (error: unknown) => void,
): LoggerInterface {
  let level = Warn;
  const logger: LoggerInterface = {
    level: ((next?: number) => {
      if (next === undefined) return level;
      level = next;
      return logger;
    }) as LoggerInterface["level"],
    error: (...args: readonly unknown[]) => {
      const [first] = args;
      onError(
        first instanceof Error
          ? first
          : new Error(args.map((arg) => String(arg)).join(" ")),
      );
      return logger;
    },
    warn: () => logger,
    info: () => logger,
    debug: () => logger,
  };
  return logger;
}

export interface EmbedFrameProps {
  spec: VisualizationSpec;
  options: EmbedOptions;
  className?: string;
  style?: CSSProperties;
  onNewView?: (view: View) => void;
  onError?: (error: unknown) => void;
  /** Rendered in place of the chart when embedding fails. */
  errorFallback?: (error: unknown) => ReactNode;
  /**
   * Re-fit the chart's width to the frame whenever the frame is resized. On
   * by default; off keeps a spec's fixed width.
   */
  trackWidth?: boolean;
}

/**
 * The embed's failure, if the current spec raised one. A failure belongs to
 * the spec that raised it: a new spec clears it, but the embed finishing does
 * not (dataflow errors are logged before it does). Keyed by content, as
 * react-vega re-embeds: an equal spec rebuilt by the parent (a re-resolved
 * theme) is still the spec that failed.
 */
function useSpecFailure(
  spec: EmbedFrameProps["spec"],
  onError: EmbedFrameProps["onError"],
): { error: unknown; handleError: (err: unknown) => void } {
  const specKey = useMemo(() => JSON.stringify(spec), [spec]);
  const [failure, setFailure] = useState<{
    error: unknown;
    spec: string;
  } | null>(null);
  const specRef = useRef(specKey);
  specRef.current = specKey;
  const error = failure?.spec === specKey ? failure.error : null;
  const handleError = useCallback(
    (err: unknown) => {
      setFailure((current) =>
        // Keep the first reason: later errors are usually its fallout.
        current?.spec === specRef.current
          ? current
          : { error: err, spec: specRef.current },
      );
      onError?.(err);
    },
    [onError],
  );

  return { error, handleError };
}

/**
 * Re-fits the embedded view to its frame's width whenever the frame is
 * resized — a Report grid cell resizes without any window `resize`, which is
 * all Vega listens for.
 */
function useRefitOnResize(
  frameRef: RefObject<HTMLDivElement | null>,
  result: Result | null,
  enabled: boolean,
): void {
  useEffect(() => {
    const element = frameRef.current;
    if (
      !enabled ||
      !element ||
      !result ||
      typeof ResizeObserver === "undefined"
    )
      return;
    // No baseline from `element.clientWidth` here: this runs only after the
    // embed resolved and React committed it, and a resize in that gap would
    // become the baseline and never be applied. The observer's initial
    // notification re-fits to the width the frame has now instead.
    let lastWidth: number | undefined;
    const observer = new ResizeObserver(() => {
      const width = element.clientWidth;
      if (width === lastWidth || width === 0) return;
      lastWidth = width;
      void result.view.width(width).runAsync();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [result, enabled]);
}

/**
 * Embeds `spec` into `ref`, forwarding dataflow errors to `handleError`.
 */
function useEmbed(
  ref: RefObject<HTMLDivElement | null>,
  spec: EmbedFrameProps["spec"],
  options: EmbedOptions,
  onNewView: EmbedFrameProps["onNewView"],
  handleError: (err: unknown) => void,
): Result | null {
  const handleEmbed = useCallback(
    (result: Result) => onNewView?.(result.view),
    [onNewView],
  );
  // Vega only logs errors raised while its dataflow runs (a transform or
  // expression failing on the rows); forward them so the chart shows why
  // instead of drawing empty axes. Stable: react-vega re-embeds when options
  // change, comparing functions by reference.
  const onErrorRef = useRef(handleError);
  onErrorRef.current = handleError;
  const logger = useMemo(
    () => forwardingLogger((err) => onErrorRef.current(err)),
    [],
  );
  const embedOptions = useMemo(
    () => ({ ...options, logger }),
    [options, logger],
  );

  return useVegaEmbed({
    ref,
    spec,
    options: embedOptions,
    onEmbed: handleEmbed,
    onError: handleError,
  });
}

/**
 * Embeds a chart, re-fits it whenever its container is resized (a Report grid
 * cell resizes without any window `resize`, which is all Vega listens for),
 * and swaps in an error state — never a crash — when embedding fails.
 */
export function EmbedFrame({
  spec,
  options,
  className,
  style,
  onNewView,
  onError,
  errorFallback,
  trackWidth = true,
}: EmbedFrameProps): ReactNode {
  const frameRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const { error, handleError } = useSpecFailure(spec, onError);
  const result = useEmbed(ref, spec, options, onNewView, handleError);

  useRefitOnResize(frameRef, result, trackWidth);

  const classes = ["erp-viz-chart", className].filter(Boolean).join(" ");
  // The embed target stays mounted through an error so that a corrected spec
  // re-embeds into it (and clears the error) without remounting the frame.
  return (
    <div ref={frameRef} className={classes} style={{ width: "100%", ...style }}>
      {error !== null && (
        <div className="erp-viz-chart--error">
          {errorFallback?.(error) ?? (
            <span className="erp-viz-error-text" role="alert">
              Unable to render chart.
            </span>
          )}
        </div>
      )}
      {/* vega-embed styles its target inline-block (sized by its content);
          `containerSize()` needs it to take the frame's width instead. */}
      <div
        ref={ref}
        style={{ display: error === null ? "block" : "none", width: "100%" }}
      />
    </div>
  );
}
